import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiUsers, FiChevronLeft, FiChevronRight,
  FiShield, FiFilter, FiX, FiMail, FiCalendar, FiAlertCircle, FiBriefcase,
} from 'react-icons/fi';
import { getAdminUsers, AdminUserRow } from '../../api/admin';

/* ── constantes ─────────────────────────────────────────────────────────────── */
const PLAN_BADGE: Record<string, string> = {
  free:       'bg-gray-100 text-gray-600',
  standard:   'bg-blue-100 text-blue-700',
  entreprise: 'bg-purple-100 text-purple-700',
  business:   'bg-orange-100 text-orange-700',
};
const ROLE_BADGE: Record<string, string> = {
  owner:  'bg-yellow-100 text-yellow-700',
  admin:  'bg-orange-100 text-orange-700',
  member: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-500',
};
const STATUS_BADGE: Record<string, string> = {
  active:        'bg-green-100 text-green-700',
  trialing:      'bg-yellow-100 text-yellow-700',
  trial_expired: 'bg-red-100 text-red-600',
  canceled:      'bg-gray-100 text-gray-500',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Actif', trialing: 'Essai', trial_expired: 'Expiré', canceled: 'Annulé',
};
const ROLES = ['owner', 'admin', 'member', 'viewer'];
const PLANS = ['free', 'standard', 'entreprise', 'business'];
const planLabel = (p: string | null) => p ? p.charAt(0).toUpperCase() + p.slice(1) : '—';
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtDateTime = (d: string) => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/* ── Avatar initiales ────────────────────────────────────────────────────────── */
const Avatar: React.FC<{ name: string; email: string }> = ({ name, email }) => {
  const initials = name
    ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : email[0].toUpperCase();
  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-indigo-500', 'bg-red-500'];
  const color = colors[(email.charCodeAt(0) + (email.charCodeAt(1) || 0)) % colors.length];
  return (
    <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
};

/* ── composant principal ─────────────────────────────────────────────────────── */
const AdminUsers: React.FC = () => {
  const [users, setUsers]         = useState<AdminUserRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [inputVal, setInputVal]   = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminUsers({
        page, limit,
        search: search || undefined,
        ...(roleFilter ? { role: roleFilter } : {}),
        ...(planFilter ? { plan: planFilter } : {}),
      } as any);
      setUsers(data.users);
      setTotal(data.total);
    } catch {
      setError('Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, planFilter]);

  useEffect(() => { load(); }, [load]);

  const handleInputChange = (val: string) => {
    setInputVal(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(val); }, 400);
  };

  const clearSearch = () => { setInputVal(''); setSearch(''); setPage(1); };
  const resetAll = () => { clearSearch(); setRoleFilter(''); setPlanFilter(''); setPage(1); };

  const totalPages = Math.ceil(total / limit);

  /* Stats rapides sur la page courante */
  const roleCounts = users.reduce<Record<string, number>>((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Utilisateurs</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} utilisateur{total !== 1 ? 's' : ''} au total</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* ── Pills rôles ── */}
      {Object.keys(roleCounts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {ROLES.filter(r => roleCounts[r]).map(r => (
            <button
              key={r}
              onClick={() => { setRoleFilter(roleFilter === r ? '' : r); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition
                ${roleFilter === r
                  ? (ROLE_BADGE[r] || 'bg-gray-100 text-gray-600') + ' border-current ring-2 ring-offset-1 ring-current'
                  : (ROLE_BADGE[r] || 'bg-gray-100 text-gray-600') + ' border-transparent hover:opacity-80'}`}
            >
              {planLabel(r)} <span className="font-bold">{roleCounts[r]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filtres ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            type="text"
            placeholder="Rechercher par nom, email ou organisation..."
            value={inputVal}
            onChange={e => handleInputChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {inputVal && (
            <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <FiX size={14} />
            </button>
          )}
        </div>
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
            <option value="">Tous les rôles</option>
            {ROLES.map(r => <option key={r} value={r}>{planLabel(r)}</option>)}
          </select>
        </div>
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
            <option value="">Tous les plans</option>
            {PLANS.map(p => <option key={p} value={p}>{planLabel(p)}</option>)}
          </select>
        </div>
        {(roleFilter || planFilter || search) && (
          <button onClick={resetAll} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition">
            <FiX size={13} /> Réinitialiser
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={16} /> {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Utilisateur</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Organisation</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Rôle</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Plan</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Dernière connexion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Chargement…
                  </div>
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Aucun utilisateur trouvé</td></tr>
              ) : users.map(user => (
                <React.Fragment key={user.id}>
                  <tr
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => setExpandedId(expandedId === user.id ? null : user.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={user.full_name} email={user.email} />
                        <div>
                          <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                            {user.full_name || '—'}
                            {user.is_super_admin && (
                              <span title="Super Admin" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">
                                <FiShield size={10} /> SA
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <FiMail size={11} /> {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                        <FiBriefcase size={12} className="text-gray-400 flex-shrink-0" />
                        <span>{user.organization_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[user.role] || 'bg-gray-100 text-gray-600'}`}>
                        {planLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_BADGE[user.subscription_plan] || 'bg-gray-100 text-gray-600'}`}>
                        {planLabel(user.subscription_plan)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${user.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                        <span className={`text-xs font-medium ${user.is_active ? 'text-green-700' : 'text-red-500'}`}>
                          {user.is_active ? 'Actif' : 'Inactif'}
                        </span>
                        {user.subscription_status && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_BADGE[user.subscription_status] || 'bg-gray-100 text-gray-500'}`}>
                            {STATUS_LABEL[user.subscription_status] || user.subscription_status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {user.last_login ? fmtDateTime(user.last_login) : <span className="text-gray-300">Jamais</span>}
                    </td>
                  </tr>

                  {/* ── Ligne expandée ── */}
                  {expandedId === user.id && (
                    <tr className="bg-blue-50/40">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">ID utilisateur</p>
                            <p className="font-mono text-xs text-gray-700 break-all">{user.id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">ID organisation</p>
                            <p className="font-mono text-xs text-gray-700 break-all">{user.organization_id || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Membre depuis</p>
                            <p className="flex items-center gap-1.5 text-gray-700">
                              <FiCalendar size={13} className="text-gray-400" />
                              {fmtDate(user.created_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Super Admin</p>
                            <p className={`font-medium ${user.is_super_admin ? 'text-red-600' : 'text-gray-400'}`}>
                              {user.is_super_admin ? '✓ Oui' : 'Non'}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <span className="text-xs text-gray-500">
              {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} sur {total}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">
                <FiChevronLeft size={14} /></button>
              <span className="px-3 py-1 text-xs text-gray-600">Page {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">
                <FiChevronRight size={14} /></button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
